import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../api/apiClient";
import type { CatalogItem } from "../../api/types";
import { looksLikeBarcode } from "../../utils/looksLikeBarcode";
import { ItemThumb } from "../ItemThumb";
import { NavIcon } from "../nav/NavIcon";
import { Chip } from "../ui/Chip";

const BarcodeScanner = lazy(() =>
  import("../BarcodeScanner").then((m) => ({ default: m.BarcodeScanner })),
);

/** How many recently received items open the list. */
const RECENT_COUNT = 12;
/** Batches read to find them — several batches are often the same item. */
const RECENT_BATCHES = 60;
const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 30;
const SEARCH_SETTLE_MS = 250;
const TOP_CATEGORIES = 4;

function byName(a: CatalogItem, b: CatalogItem): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
}

/**
 * The receive window's "what arrived?" step (owner, 2026-10-09): the native
 * drop-down of the whole catalogue gave way to a search like the Inventory
 * page's. Typing searches the server (name, SKU, the supplier barcode and the
 * shop's own printed code); Scan looks a code up and picks it when exactly one
 * item carries it — a code can name several (G-61), then they are listed to
 * tap. With nothing typed the list opens on what this shop received lately;
 * category chips narrow it.
 */
export function ReceiveItemFinder({
  locationId,
  selected,
  onSelect,
  stockByItem,
  newItemHref,
}: {
  locationId: string;
  /** The item picked so far, resolved by the window (a preset arrives as an id). */
  selected: CatalogItem | null;
  onSelect: (itemId: string) => void;
  /** On-hand quantity at this shop, by item, when the page knows it. */
  stockByItem?: ReadonlyMap<string, number>;
  /** Where "New item" goes, carrying what was searched for; absent for people who cannot create items. */
  newItemHref?: (seed?: string) => string;
}) {
  const [catalog, setCatalog] = useState<CatalogItem[] | null>(null);
  const [recentIds, setRecentIds] = useState<string[]>([]);
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [found, setFound] = useState<CatalogItem[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanNote, setScanNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .listItems()
      .then((rows) => {
        if (!cancelled) setCatalog(rows);
      })
      .catch(() => {
        if (!cancelled) setCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void api
      .listReceivedBatches({ locationId, limit: RECENT_BATCHES })
      .then((res) => {
        if (cancelled) return;
        const ids: string[] = [];
        for (const row of res.items) {
          if (!ids.includes(row.item.id)) ids.push(row.item.id);
          if (ids.length >= RECENT_COUNT) break;
        }
        setRecentIds(ids);
      })
      .catch(() => {
        if (!cancelled) setRecentIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const typed = query.trim();
  useEffect(() => {
    if (typed.length < SEARCH_MIN_CHARS) {
      setFound(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      void api
        .searchItems(typed, SEARCH_LIMIT)
        .then((rows) => {
          if (!cancelled) setFound(rows);
        })
        .catch(() => {
          if (!cancelled) setFound([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, SEARCH_SETTLE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [typed]);

  /** The busiest categories in the catalogue, then "More" for the rest. */
  const categories = useMemo(() => {
    const counts = new Map<string, { id: string; label: string; count: number }>();
    for (const item of catalog ?? []) {
      const c = counts.get(item.category.id);
      if (c) c.count += 1;
      else counts.set(item.category.id, { id: item.category.id, label: item.category.label, count: 1 });
    }
    return [...counts.values()].sort(
      (a, b) => b.count - a.count || a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [catalog]);
  const topCategories = categories.slice(0, TOP_CATEGORIES);
  const chosenCategory = categories.find((c) => c.id === categoryId);

  const list = useMemo((): CatalogItem[] | null => {
    const inCategory = (item: CatalogItem) => !categoryId || item.category.id === categoryId;
    if (typed.length >= SEARCH_MIN_CHARS) return found ? found.filter(inCategory) : null;
    if (!catalog) return null;
    if (categoryId) return catalog.filter(inCategory).sort(byName);
    const byId = new Map(catalog.map((item) => [item.id, item]));
    return recentIds.map((id) => byId.get(id)).filter((item): item is CatalogItem => Boolean(item));
  }, [typed, found, catalog, categoryId, recentIds]);

  const onScanned = async (code: string) => {
    setScanning(false);
    setScanNote(null);
    setCategoryId("");
    setQuery(code);
    try {
      const rows = await api.searchItems(code, SEARCH_LIMIT);
      // A scan means THIS code: prefer exact matches over items that merely contain it.
      const exact = rows.filter((item) => item.barcode === code);
      const matches = exact.length > 0 ? exact : rows;
      if (matches.length === 1) {
        onSelect(matches[0]!.id);
        setQuery("");
      } else {
        setFound(matches);
        if (matches.length > 1) setScanNote(`${matches.length} items carry this code — tap the one that arrived.`);
      }
    } catch {
      setFound([]);
    }
  };

  if (selected) {
    return (
      <div className="rif-chosen">
        <ItemThumb itemId={selected.id} imageKey={selected.imageKey ?? null} alt="" size={48} />
        <div className="rif-chosen-text">
          <span className="rif-name">{selected.name}</span>
          <span className="rif-meta">
            {selected.category.label} · {selected.defaultUom}
            {stockByItem?.has(selected.id) ? ` · ${stockByItem.get(selected.id)} in stock here` : ""}
          </span>
        </div>
        <button type="button" className="rif-change" onClick={() => onSelect("")}>
          Change
        </button>
      </div>
    );
  }

  const heading = typed.length >= SEARCH_MIN_CHARS
    ? "Results"
    : chosenCategory
      ? chosenCategory.label
      : "Recently received here";

  return (
    <div className="rif">
      <div className="inv-search-row">
        <label className="inv-search-field" htmlFor="receive-item-search">
          <span className="visually-hidden">Find the item that arrived</span>
          <NavIcon name="search" size={20} />
          <input
            id="receive-item-search"
            type="search"
            className="inv-search-input"
            placeholder="Name, barcode, SKU"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setScanNote(null);
            }}
            autoComplete="off"
            autoFocus
          />
        </label>
        <button
          type="button"
          className="inv-scan-tile"
          onClick={() => setScanning(true)}
          aria-label="Scan the item's barcode"
        >
          <NavIcon name="barcode" size={22} />
          <span>Scan</span>
        </button>
      </div>

      {scanning ? (
        <Suspense fallback={null}>
          <BarcodeScanner onDetected={(code) => void onScanned(code)} onClose={() => setScanning(false)} />
        </Suspense>
      ) : null}

      {categories.length > 0 ? (
        <div className="inv-filter-chips" role="group" aria-label="Filter by category">
          <Chip label="Recent" selected={!categoryId} onClick={() => setCategoryId("")} />
          {topCategories.map((c) => (
            <Chip
              key={c.id}
              label={c.label}
              count={c.count}
              selected={categoryId === c.id}
              onClick={() => setCategoryId(categoryId === c.id ? "" : c.id)}
            />
          ))}
          {chosenCategory && !topCategories.some((c) => c.id === categoryId) ? (
            <Chip label={chosenCategory.label} icon="close" selected onClick={() => setCategoryId("")} />
          ) : null}
          <span className="inv-more">
            <select
              className="kit-chip inv-more-select"
              aria-label="More categories"
              value=""
              onChange={(e) => setCategoryId(e.target.value)}
            >
              <option value="">More</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <NavIcon name="chevronDown" size={14} />
          </span>
        </div>
      ) : null}

      <div className="rif-head">{heading}</div>
      {scanNote ? <p className="rif-note">{scanNote}</p> : null}

      {list === null || (searching && !found) ? (
        <p className="muted rif-empty">Loading…</p>
      ) : list.length === 0 ? (
        <div className="rif-empty">
          <p className="muted">
            {typed.length >= SEARCH_MIN_CHARS
              ? `Nothing matches “${typed}”.`
              : chosenCategory
                ? "No items in this category."
                : "Nothing received here yet — search or scan to find the item."}
          </p>
          {newItemHref && typed.length >= SEARCH_MIN_CHARS ? (
            <Link className="button tinted" to={newItemHref(typed)}>
              {looksLikeBarcode(typed) ? `New item with barcode ${typed}` : `New item “${typed}”`}
            </Link>
          ) : null}
        </div>
      ) : (
        <ul className="rif-list">
          {list.map((item) => (
            <li key={item.id}>
              <button type="button" className="rif-row" onClick={() => onSelect(item.id)}>
                <ItemThumb itemId={item.id} imageKey={item.imageKey ?? null} alt="" size={44} />
                <span className="rif-row-text">
                  <span className="rif-name">{item.name}</span>
                  <span className="rif-meta">
                    {item.category.label} · {item.defaultUom}
                    {item.color ? ` · ${item.color.name}` : ""}
                  </span>
                </span>
                {stockByItem?.has(item.id) ? (
                  <span className="rif-stock">
                    <b>{stockByItem.get(item.id)}</b>
                    <span>here</span>
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
